import { regionStore } from '../store/jotai/store';
import { basketAtom } from '../store/jotai/atoms/basket.atom';
import { strapiClient } from '../core/api/strapi-client';
import { StrapiBasket } from '../core/models/strapi-basket.model';
import { StrapiBasketItem } from '../core/models/strapi-basket-item.model';
import { StrapiProduct } from '../core/models/strapi-product.model';
import { getLogo } from '../utils/imageUtils';
import { Basket, VendorBasketEntry } from '../models/basket.model';
import { Logger } from '../core/logger/Logger';
import { SHIPPING_TYPES } from '../core/models/strapi-shipping-rate.model';

const mapToVendorBasketEntry = (item: StrapiBasketItem): VendorBasketEntry => ({
  id: item.product.vendor.id,
  title: item.product.vendor.title,
  items: [],
  urlSegment: item.product.vendor.urlSegment,
  logoUrl: getLogo(item.product.vendor)
});

const sortBasketItems = (basketEntries: VendorBasketEntry[]) =>
  basketEntries.map((vendor) => {
    const sortedItems = vendor.items.sort((a, b) => {
      if (a.product.inactive && !b.product.inactive) return 1;
      if (!a.product.inactive && b.product.inactive) return -1;
      return 0;
    });

    return { ...vendor, items: sortedItems };
  });

const mapBasketItems = (items: StrapiBasketItem[]) => {
  const basketItems = new Map<number, VendorBasketEntry>();
  const vendorsIds = new Set<number>();

  items?.forEach((item) => {
    const vendorId = item.product.vendor.id;
    vendorsIds.add(vendorId);
    if (!basketItems.has(vendorId)) {
      basketItems.set(vendorId, mapToVendorBasketEntry(item));
    }
    basketItems.get(vendorId)?.items.push(item);
  });

  let basketItemsArray = Array.from(basketItems.values());
  basketItemsArray = sortBasketItems(basketItemsArray);

  return basketItemsArray;
};

const calculateTotal = (basketItems: StrapiBasketItem[]): number => {
  const filteredItems = basketItems.filter(
    (item) =>
      item.product.availableQuantity > 0 && !item.product.inactive && !item.product.requestOnly
  );

  return filteredItems.reduce(
    (acc, item) =>
      acc + item.quantity * (item.product.price + item.product?.shipping_rates?.[0]?.rate || 0),
    0
  );
};

/**
 * Calculate total taking into account vendor-level shipping rules:
 * - freeShipping overrides all if minOrderAmount is met
 * - flatrate overrides per-product shipping
 * - otherwise per-product shipping rates apply
 */
const calculateTotalFromVendorEntries = (
  vendorEntries: VendorBasketEntry[],
  sendcloudSelections?: Record<string, { optionCode: string; methodName: string; price: number }>
): number => {
  return vendorEntries.reduce((total, vendor) => {
    const availableItems = vendor.items.filter(
      (item) =>
        item.product.availableQuantity > 0 && !item.product.inactive && !item.product.requestOnly
    );

    if (!availableItems.length) return total;

    const productSubtotal = availableItems.reduce(
      (acc, item) => acc + item.quantity * item.product.price,
      0
    );

    // Sendcloud vendor: use selected Sendcloud price, or exclude shipping if not yet selected
    const isSendcloudVendor = availableItems[0]?.product?.vendor?.useSendcloudShipping;
    if (isSendcloudVendor) {
      const scSelection = sendcloudSelections?.[String(vendor.id)];
      return total + productSubtotal + (scSelection?.price || 0);
    }

    const vendorShippingRates = availableItems[0]?.product?.vendor?.shipping_rates || [];
    const freeShippingRate = vendorShippingRates.find(
      (r: any) => r.shippingType === SHIPPING_TYPES.FREE_SHIPPING
    );
    const flatrateRate = vendorShippingRates.find(
      (r: any) => r.shippingType === SHIPPING_TYPES.FLATRATE
    );

    // Free shipping if minOrderAmount is met
    if (freeShippingRate) {
      const minAmount = freeShippingRate.minOrderAmount || 0;
      if (minAmount === 0 || productSubtotal >= minAmount) {
        return total + productSubtotal;
      }
    }

    // Flatrate overrides per-product shipping
    if (flatrateRate) {
      return total + productSubtotal + flatrateRate.rate;
    }

    // Default: per-product shipping rates
    const shippingTotal = availableItems.reduce(
      (acc, item) => acc + item.quantity * (item.product?.shipping_rates?.[0]?.rate || 0),
      0
    );

    return total + productSubtotal + shippingTotal;
  }, 0);
};

const updateBasketItemQuantity = (id: number, basketItem: StrapiBasketItem) =>
  strapiClient
    .update<StrapiBasketItem, StrapiBasketItem>('basket-items', id, basketItem)
    .then((data) => {
      if (!data) {
        throw new Error('updateBasketItemQuantity failed');
      }

      const basket = regionStore.get(basketAtom);
      if (!basket) {
        throw new Error('Basket not found');
      }

      let basketItems = basket?.basket_items;

      basketItems = basketItems.map((item: VendorBasketEntry) => ({
        ...item,
        items: item.items.map((bItem) => {
          if (bItem.id === id) {
            return { ...bItem, quantity: basketItem.quantity };
          }
          return bItem;
        })
      }));

      const total = basketItems?.length ? calculateTotalFromVendorEntries(basketItems) : 0;
      const updatedBasket = { ...basket, basket_items: basketItems, total };

      regionStore.set(basketAtom, updatedBasket);
    });

const deleteBasketItem = (id: number) =>
  strapiClient.delete(id, 'basket-items').then(() => {
    const basket = regionStore.get(basketAtom);
    if (!basket) {
      throw new Error('Basket not found');
    }

    const basketItems = basket?.basket_items || [];
    const updatedBasketItems = basketItems.map((item: VendorBasketEntry) => ({
      ...item,
      items: item.items.filter((bItem) => bItem.id !== id)
    }));

    const filteredBasketItems = updatedBasketItems.filter((item) => item.items.length > 0);
    const total = calculateTotalFromVendorEntries(filteredBasketItems);
    const updatedBasket = { ...basket, total, basket_items: filteredBasketItems };

    regionStore.set(basketAtom, updatedBasket);
  });

const getBasket = (): Promise<Basket> =>
  strapiClient
    .fetch<StrapiBasket>({ entityPath: 'baskets' })
    .then((response) => {
      if (!response.data) {
        throw new Error('getBasket failed');
      }

      const mappedBasket = mapBasketItems(response.data.basket_items);
      const total = mappedBasket?.length ? calculateTotalFromVendorEntries(mappedBasket) : 0;

      const basket = {
        id: response.data.id,
        total,
        basket_items: mappedBasket
      };
      regionStore.set(basketAtom, basket);
      return basket;
    })
    .catch((error) => {
      Logger.error('Error fetching basket:', error);
      throw new Error('Failed to fetch basket');
    });

const addToBasket = (product: StrapiProduct, quantity: number) =>
  strapiClient
    .create<Partial<StrapiBasketItem>, StrapiBasketItem>(`basket-items`, {
      product,
      quantity
    })
    .then((response) => {
      if (!response.data) {
        throw new Error('addToBasket failed');
      }

      const basketItem = response.data;
      const basket = regionStore.get(basketAtom);

      if (!basket) {
        throw new Error('Basket not found');
      }

      const existingItem = basket.basket_items
        ?.flatMap((el) => el.items)
        .find((item) => item.id === basketItem.id);

      if (existingItem) {
        const updatedBasketItems = basket.basket_items.map((bItem) => ({
          ...bItem,
          items: bItem.items.map((item) =>
            item.id === existingItem.id ? { ...item, quantity: basketItem.quantity } : item
          )
        }));

        const total = calculateTotalFromVendorEntries(updatedBasketItems);
        const updatedBasket: Basket = { ...basket, basket_items: updatedBasketItems, total };

        regionStore.set(basketAtom, updatedBasket);
      } else {
        let updatedBasket: Basket;
        const vendorExists = basket.basket_items.some(
          (item) => item.id === basketItem.product.vendor.id
        );

        if (!vendorExists) {
          const newVendorEntry = mapToVendorBasketEntry(basketItem);
          newVendorEntry.items.push(basketItem);
          updatedBasket = basket;
          updatedBasket.basket_items.push(newVendorEntry);
        } else {
          updatedBasket = {
            ...basket,
            basket_items: basket.basket_items.map((item) => {
              if (item.id === basketItem.product.vendor.id) {
                return {
                  ...item,
                  items: [...item.items, basketItem]
                };
              }
              return item;
            })
          };
        }

        updatedBasket.total = updatedBasket.basket_items.length
          ? calculateTotalFromVendorEntries(updatedBasket.basket_items)
          : 0;

        regionStore.set(basketAtom, {
          ...updatedBasket,
          basket_items: sortBasketItems(updatedBasket.basket_items)
        });
      }
    });

export const basketService = {
  getBasket,
  addToBasket,
  updateBasketItemQuantity,
  deleteBasketItem
};

export { calculateTotalFromVendorEntries };

/** @internal — exported for unit tests only */
export const _test = { calculateTotalFromVendorEntries, mapBasketItems };

export type BasketService = {
  getBasket: () => Promise<void>;
  addToBasket: (productId: number, quantity: number) => Promise<void>;
  updateBasketItemQuantity: (id: number, quantity: number) => Promise<void>;
  deleteBasketItem: (id: number) => Promise<void>;
};
